import React, { useState, useEffect, useRef } from 'react';
import { Camera, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { MemberQuickNav } from '../MemberQuickNav';
import api from '../../lib/api';

const FOCUS_AREAS = ['Art and Culture', 'Civic Engagement and Outreach', 'Education and Libraries', 'Environment', 'Health and Wellness'];
const COMMITTEES = ['Events', 'Community Service', 'Social Media', 'Membership', 'Board Leadership', 'Project Groups (Five Focus Areas)', 'Not at this time'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', 'Other'];
const DIRECTORY_FIELDS = [
  { key: 'full_name', label: 'Full name' },
  { key: 'preferred_name', label: 'Preferred name' },
  { key: 'phone', label: 'Telephone number' },
  { key: 'email', label: 'Email address' },
  { key: 'mailing_address', label: 'Mailing address' },
  { key: 'profession', label: 'Profession or area of expertise' },
];

const Section = ({ title, children }) => (
  <section className="bg-white rounded-xl shadow-sm border border-[#bd8cae]/20 p-6 sm:p-8">
    <h2 className="font-serif text-xl text-[#3c4a2a] mb-5">{title}</h2>
    <div className="space-y-5">{children}</div>
  </section>
);

const PillGroup = ({ label, options, value, onChange, testId }) => (
  <div>
    <Label className="text-[#3c4a2a]">{label}</Label>
    <div className="flex flex-wrap gap-2 mt-2" data-testid={testId}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(value === opt ? '' : opt)}
          className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
            value === opt ? 'bg-[#5d6b3c] text-white border-[#5d6b3c]' : 'bg-white text-[#3c4a2a] border-[#bd8cae]/50 hover:border-[#5d6b3c]'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const CheckGroup = ({ label, options, values, onChange, testId, getKey = (o) => o, getLabel = (o) => o }) => (
  <div>
    <Label className="text-[#3c4a2a]">{label}</Label>
    <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid={testId}>
      {options.map((opt) => {
        const k = getKey(opt);
        const checked = values.includes(k);
        return (
          <label key={k} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${checked ? 'border-[#5d6b3c] bg-[#5d6b3c]/5' : 'border-[#bd8cae]/40'}`}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onChange(checked ? values.filter((v) => v !== k) : [...values, k])}
              className="accent-[#5d6b3c]"
            />
            <span className="text-[#3c4a2a]">{getLabel(opt)}</span>
          </label>
        );
      })}
    </div>
  </div>
);

const Field = ({ label, value, onChange, testId, type = 'text', placeholder = '' }) => (
  <div>
    <Label className="text-[#3c4a2a]">{label}</Label>
    <Input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 bg-white" />
  </div>
);

export default function MemberInfoFormPage() {
  const [p, setP] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/members/me/profile').then((res) => {
      setP(res.data.profile);
      setPhotoUrl(res.data.photo_url);
    }).catch(() => toast.error('Could not load your information.'));
  }, []);

  const set = (key) => (val) => setP((prev) => ({ ...prev, [key]: val }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/members/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoUrl(`${res.data.photo_url}?t=${Date.now()}`);
      toast.success('Photo uploaded!');
    } catch {
      toast.error('Photo upload failed — please try a smaller image.');
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/members/me/profile', p);
      toast.success('Your information has been saved.');
    } catch {
      toast.error('Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!p) return <main className="min-h-screen bg-[#f1e3eb] pt-44 text-center text-[#3c4a2a]/50">Loading…</main>;

  const backendBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <main className="min-h-screen bg-[#f1e3eb] pt-44 pb-20 px-4" data-testid="member-info-page">
      <div className="max-w-3xl mx-auto">
        <MemberQuickNav />
        <div className="text-center mb-10">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">Members Only</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-2">Member Information Form</h1>
          <p className="text-[#3c4a2a]/70 mt-3">Tell us about yourself — you control exactly what appears in the member directory.</p>
        </div>

        <form onSubmit={save} className="space-y-6">
          <Section title="Your Photo">
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-[#bd8cae]/20 flex items-center justify-center flex-shrink-0">
                {photoUrl ? (
                  <img src={`${backendBase}${photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`}`} alt="Your profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                ) : (
                  <Camera className="w-8 h-8 text-[#a3688f]" />
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="profile-photo-input" />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="profile-photo-btn" className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white">
                  <Camera className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading…' : photoUrl ? 'Change Photo' : 'Upload a Photo'}
                </Button>
                <p className="text-xs text-[#3c4a2a]/50 mt-2">Shown on your directory listing. JPEG/PNG, under 10 MB.</p>
              </div>
            </div>
          </Section>

          <Section title="Personal Information">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" value={p.full_name} onChange={set('full_name')} testId="info-full-name" />
              <Field label="Preferred name" value={p.preferred_name} onChange={set('preferred_name')} testId="info-preferred-name" />
              <Field label="Preferred pronouns (optional)" value={p.pronouns} onChange={set('pronouns')} testId="info-pronouns" />
              <Field label="Telephone number" value={p.phone} onChange={set('phone')} testId="info-phone" />
              <Field label="Email address" value={p.email} onChange={set('email')} testId="info-email" />
              <Field label="Profession or area of expertise (optional)" value={p.profession} onChange={set('profession')} testId="info-profession" />
              <Field label="Birthday (month/day, optional)" value={p.birthday} onChange={set('birthday')} testId="info-birthday" placeholder="e.g., 04/12" />
            </div>
            <div>
              <Label className="text-[#3c4a2a]">Mailing address</Label>
              <Textarea rows={2} value={p.mailing_address} onChange={(e) => set('mailing_address')(e.target.value)} data-testid="info-mailing-address" className="mt-1 bg-white" />
            </div>
            <PillGroup label="Are you married? (optional)" options={['Yes', 'No', 'Prefer not to answer']} value={p.married} onChange={set('married')} testId="info-married" />
            <PillGroup label="Do you have children? (optional)" options={['Yes', 'No', 'Prefer not to answer']} value={p.children} onChange={set('children')} testId="info-children" />
          </Section>

          <Section title="Club Anniversary">
            <Field label="Date you joined the club (if known)" type="date" value={p.joined_date} onChange={set('joined_date')} testId="info-joined-date" />
          </Section>

          <Section title="Interests & Involvement">
            <CheckGroup label="Which focus areas interest you?" options={FOCUS_AREAS} values={p.focus_areas || []} onChange={set('focus_areas')} testId="info-focus-areas" />
            <PillGroup label="Would you like to contribute articles or help with the club newsletter?" options={['Yes', 'Maybe — send more information', 'Not at this time']} value={p.newsletter_help} onChange={set('newsletter_help')} testId="info-newsletter" />
            <PillGroup label="Would you like to participate in the book club?" options={['Yes', 'Maybe — send more information', 'Not at this time']} value={p.book_club} onChange={set('book_club')} testId="info-book-club" />
            <CheckGroup label="Which committees or leadership opportunities interest you?" options={COMMITTEES} values={p.committees || []} onChange={set('committees')} testId="info-committees" />
            <div>
              <Label className="text-[#3c4a2a]">Skills, hobbies, or talents you would enjoy sharing</Label>
              <Textarea rows={3} value={p.skills} onChange={(e) => set('skills')(e.target.value)} data-testid="info-skills" className="mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[#3c4a2a]">Ideas for club activities, speakers, or projects</Label>
              <Textarea rows={3} value={p.ideas} onChange={(e) => set('ideas')(e.target.value)} data-testid="info-ideas" className="mt-1 bg-white" />
            </div>
          </Section>

          <Section title="T-Shirt Preferences">
            <PillGroup label="Preferred fit" options={["Women's", 'Unisex']} value={p.shirt_fit} onChange={set('shirt_fit')} testId="info-shirt-fit" />
            <PillGroup label="Size" options={SIZES} value={p.shirt_size} onChange={set('shirt_size')} testId="info-shirt-size" />
            {p.shirt_size === 'Other' && (
              <Field label="Other size" value={p.shirt_size_other} onChange={set('shirt_size_other')} testId="info-shirt-size-other" />
            )}
            <p className="text-xs text-[#3c4a2a]/50">Please refer to the provided size chart when ordering.</p>
          </Section>

          <Section title="Photo Consent">
            <PillGroup label="May the club use photos of you in member-only communications, including its internal newsletter?" options={['Yes', 'No']} value={p.photo_internal} onChange={set('photo_internal')} testId="info-photo-internal" />
            <PillGroup label="May the club use photos of you in public communications, including its website, public newsletter, social media, and promotional materials?" options={['Yes', 'No']} value={p.photo_public} onChange={set('photo_public')} testId="info-photo-public" />
          </Section>

          <Section title="Member Directory Consent">
            <PillGroup label="Would you like to be included in a directory shared only with current club members?" options={['Yes', 'No']} value={p.directory_consent} onChange={set('directory_consent')} testId="info-directory-consent" />
            {p.directory_consent === 'Yes' && (
              <CheckGroup
                label="Select the information you authorize us to include:"
                options={DIRECTORY_FIELDS}
                values={p.directory_fields || []}
                onChange={set('directory_fields')}
                getKey={(o) => o.key}
                getLabel={(o) => o.label}
                testId="info-directory-fields"
              />
            )}
            <p className="text-xs text-[#3c4a2a]/60">
              Your information will be used to manage membership, communicate about club activities, and help plan programs. Directory
              information and photos will be shared according to your selections above. You may update your information or change your
              consent preferences here at any time.
            </p>
          </Section>

          <Button type="submit" disabled={saving} data-testid="info-save-btn" className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-6 text-base">
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving…' : 'Save My Information'}
          </Button>
        </form>
      </div>
    </main>
  );
}
